<template>
  <ErrorBoundary>
    <ClientOnly>
      <div v-if="!loading" class="little-academy">
        <div v-if="isHaveFullLayout">
          <academy-header />
          <div class="academy-screen">
            <academy-left-sidebar />
            <div
              :class="[
                'academy-screen__content',
                {
                  'justify-center': isCentered,
                },
              ]"
            >
              <div class="max-w-[1600px] xl:w-[944px] min-w-[944px]">
                <nuxt />
              </div>
            </div>
          </div>
        </div>
        <div v-else class="w-screen h-screen">
          <nuxt />
        </div>
        <portal-target multiple name="app-portal"></portal-target>
      </div>
    </ClientOnly>
  </ErrorBoundary>
</template>

<script>
import { mapActions } from 'vuex'

import jwtDecode from 'jwt-decode'
import AcademyHeader from '~/components/layout/Header'
import AcademyLeftSidebar from '~/components/layout/Sidebar'
import ErrorBoundary from '~/components/v2/ErrorBoundary.vue'
import { organisationInit } from '~/mixins/organisationInit'

const TWO_MINUTES = 2 * 60 * 1000
const DEFAULT_INTERVAL_MINUTES =
  (['development', 'staging'].includes(process.env.NUXT_ENV_ENV_NAME) ? 5 : 55) * 60 * 1000

export default {
  name: 'DefaultLayout',
  components: {
    AcademyHeader,
    AcademyLeftSidebar,
    ErrorBoundary,
  },

  mixins: [organisationInit],

  data() {
    return {
      loading: true,
    }
  },

  computed: {
    isHaveFullLayout() {
      return ![
        'not-permission',
        'performance-appraisal-detail-v2-appraisalId',
        'performance-v2-edit-checklist-appraisalId',
        'admin-performance-appraisal-detail-v2-appraisalId',
        'performance-print-pdf',
        'reflection-weekly-form-id',
        'api-healthz',
      ].includes(this.$get(this.$route, 'name', ''))
    },
    isCentered() {
      return !['performance-v2-edit-checklist-appraisalId'].includes(this.$get(this.$route, 'name', ''))
    },
  },

  methods: {
    ...mapActions({
      checkForUpdates: 'app/checkForUpdates',
      refreshToken: 'profile/REFRESH_USER_TOKEN',
    }),
  },

  created() {
    if (process.browser) {
      window.dataLayer = window.dataLayer || []
      function gtag() {
        dataLayer.push(arguments)
      }
      gtag('js', new Date())
      gtag('config', process.env.GOOGLE_ANALYTICS_ID)
    }
  },

  mounted() {
    try {
      const internalToken = this.$cookie.get('laAccessToken')
      const externalToken = this.$cookie.get('accessToken')
      const accessToken = internalToken || externalToken
      
      if (accessToken) {
        const decodedAccessToken = jwtDecode(accessToken)
        if (decodedAccessToken) {
          const interval = decodedAccessToken.exp * 1000 - Date.now() - TWO_MINUTES
          setInterval(
            () => {
              // Refresh token if available
              if (
                (internalToken && this.$cookie.get('laRefreshToken')) ||
                (externalToken && !internalToken)
              ) {
                this.refreshToken()
              }
            },
            interval > TWO_MINUTES ? interval : DEFAULT_INTERVAL_MINUTES
          )
        }
      } else if (refreshToken) {
        // If no access token but refresh token exists, try to refresh the token
        this.refreshToken()
      }
      
      if (process.env.NUXT_ENV_ENV_NAME !== 'development') {
        this.checkForUpdates()
      }
      this.$nextTick(function () {
        this.loading = false
      })
    } catch (err) {
      this.$errorProcessor.capture(`Error on mounted layout ${err}`, { errorLocation: this.$route.path })
    }
  },
}
</script>

<style lang="scss">
.academy-screen {
  display: flex;
  position: relative;
  min-height: 100vh;
  overflow: auto;
}

.academy-screen__content {
  display: flex;
  margin: 60px 0 100px;
  padding: 36px 64px 36px calc(240px + 48px); // add left-sidebar width
  position: relative;
  flex: 1;
  transition: all 0.4s ease-in-out;

  &.is-sidebar-open {
    margin-right: 250px;
  }

  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: #cccccc;
  }

  @media screen and (max-width: 1210px) {
    padding-left: 100px;
    padding-right: 20px;
  }

  @media screen and (max-width: 540px) {
    padding-left: 68px;
    padding-right: 20px;
  }

  &--no-layout {
    margin: 0;
    padding: 0;
    width: 100vw;
  }
}

.little-academy {
  width: 100%;
}

#__nuxt,
#__layout {
  height: 100%;
}
</style>
